import { useState, useEffect, useCallback, memo, lazy, Suspense, useRef } from 'react';

// ── Lazy load das seções abaixo do fold ──────────────────────────────────────
const ReviewsSection = lazy(() => import('./components/ReviewsSection'));
const FaqSection     = lazy(() => import('./components/FaqSection'));
const ContactSection = lazy(() => import('./components/ContactSection'));
const FooterSection  = lazy(() => import('./components/FooterSection'));

// ── Fallback leve para Suspense ───────────────────────────────────────────────
function SectionSkeleton() {
  return (
    <div className="py-16 sm:py-24 flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-[#FF6B35]/30 border-t-[#FF6B35] rounded-full animate-spin" />
    </div>
  );
}

// ── Hook: detecta quando elemento entra na viewport ──────────────────────────
function useInView(rootMargin = '200px') {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { rootMargin }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [rootMargin]);
  return { ref, inView };
}

// ── ServiceCard ───────────────────────────────────────────────────────────────
const ServiceCard = memo(({ icon, title, badge, badgeColor, items, bgFrom, borderClass, iconBg, iconColor, mt }: {
  icon: string; title: string; badge?: string; badgeColor?: string;
  items: string[]; bgFrom: string; borderClass?: string; iconBg: string; iconColor: string; mt?: boolean;
}) => (
  <div className={`bg-gradient-to-b ${bgFrom} to-white rounded-3xl p-8 hover:shadow-xl transition-shadow duration-300 cursor-pointer ${borderClass || ''} ${mt ? 'md:mt-8' : ''}`}>
    <div className={`w-16 h-16 ${iconBg} rounded-full flex items-center justify-center mb-6`}>
      <i className={`${icon} text-3xl ${iconColor}`}></i>
    </div>
    <h3 className="text-2xl font-bold text-[#1A365D] mb-1">{title}</h3>
    {badge && <p className={`${badgeColor} text-sm font-semibold mb-4 uppercase tracking-wide`}>{badge}</p>}
    {!badge && <div className="mb-4" />}
    <ul className="space-y-3">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-3">
          <i className="ri-checkbox-circle-fill text-[#FF6B35] text-xl mt-0.5 flex-shrink-0"></i>
          <span className="text-[#2D3748]">{item}</span>
        </li>
      ))}
    </ul>
  </div>
));

// ── Componente principal ──────────────────────────────────────────────────────
export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen]     = useState(false);

  // Detectores de visibilidade para cada seção lazy
  const reviewsView = useInView('300px');
  const faqView     = useInView('300px');
  const contactView = useInView('300px');
  const footerView  = useInView('100px');

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => { if (window.innerWidth >= 768) setMenuOpen(false); };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const scrollToSection = useCallback((id: string) => {
    setMenuOpen(false);
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, []);

  return (
    <div className="min-h-screen bg-white">

      {/* ── Navegação ── */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled || menuOpen ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src="https://static.readdy.ai/image/796b7ef0b947351af991ad3f5bace360/2ecc33d877b9f4a34b097237c00d9526.jpeg"
                alt="Altha Services Logo"
                className="h-11 w-11 object-cover rounded-full"
                width={44} height={44}
                loading="eager"
                decoding="sync"
              />
              <span className={`text-xl font-bold transition-colors ${isScrolled || menuOpen ? 'text-[#1A365D]' : 'text-white'}`}>
                <span className="text-[#FF6B35] tracking-widest font-extrabold">ALTHA</span>{' '}
                <span className="font-light tracking-wide">Services</span>
              </span>
            </div>

            {/* Desktop */}
            <div className="hidden md:flex items-center gap-8">
              {['home','about','services','contact'].map((s, i) => (
                <button key={s} onClick={() => scrollToSection(s)}
                  className={`font-medium transition-colors hover:text-[#FF6B35] whitespace-nowrap cursor-pointer min-h-[44px] px-2 ${isScrolled ? 'text-[#2D3748]' : 'text-white'}`}>
                  {['Home','About Us','Services','Contact'][i]}
                </button>
              ))}
              <button onClick={() => scrollToSection('contact')}
                className="bg-[#FF6B35] text-white px-6 py-3 rounded-full font-medium hover:bg-[#E55A2B] transition-colors whitespace-nowrap cursor-pointer min-h-[44px]">
                Get a Quote
              </button>
            </div>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMenuOpen(prev => !prev)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              className={`md:hidden w-11 h-11 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${isScrolled || menuOpen ? 'text-[#1A365D]' : 'text-white'}`}
            >
              <i className={`text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>

          {menuOpen && (
            <div className="md:hidden mt-3 pb-4 border-t border-gray-100 pt-4 flex flex-col gap-1">
              {['home','about','services','contact'].map((s, i) => (
                <button key={s} onClick={() => scrollToSection(s)}
                  className="text-left text-[#2D3748] font-medium py-3 px-2 rounded-xl hover:bg-[#FF6B35]/10 hover:text-[#FF6B35] transition-colors cursor-pointer min-h-[48px] text-base">
                  {['Home','About Us','Services','Contact'][i]}
                </button>
              ))}
              <button onClick={() => scrollToSection('contact')}
                className="mt-2 bg-[#FF6B35] text-white py-4 rounded-full font-semibold text-base hover:bg-[#E55A2B] transition-colors cursor-pointer min-h-[52px]">
                Get a Free Quote
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* ── Hero ── */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/796b7ef0b947351af991ad3f5bace360/41ffdd9353a0024512185626fc961efc.jpeg"
            alt="Altha Services Team"
            className="w-full h-full object-cover object-top"
            loading="eager"
            fetchPriority="high"
            decoding="sync"
            width={1200}
            height={800}
          />
          <div className="absolute inset-0 bg-black/30"></div>
        </div>

        <div className="relative z-10 w-full px-4 sm:px-6 py-32">
          <div className="max-w-2xl mx-auto text-center">
            <span className="inline-block bg-white/15 backdrop-blur-sm text-white text-xs font-semibold tracking-widest uppercase px-5 py-2 rounded-full mb-8 border border-white/25">
              Orlando&apos;s #1 Cleaning Company
            </span>
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold text-white mb-6 leading-tight tracking-tight">
              Clean Spaces.<br />
              <span className="text-[#FF6B35]">Happy Lives.</span>
            </h1>
            <p className="text-base sm:text-lg text-white/80 mb-10 leading-relaxed">
              Professional cleaning for homes, offices & Airbnbs across Orlando.<br className="hidden sm:block" />
              Trusted by 500+ clients — pet friendly & eco-conscious.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button onClick={() => scrollToSection('contact')}
                className="bg-[#FF6B35] text-white px-8 py-4 rounded-full font-semibold text-base hover:bg-[#E55A2B] active:bg-[#C94E22] transition-colors inline-flex items-center gap-2 whitespace-nowrap cursor-pointer min-h-[52px] w-full sm:w-auto justify-center">
                Get a Free Quote
                <i className="ri-arrow-right-line text-lg"></i>
              </button>
              <button onClick={() => scrollToSection('services')}
                className="bg-white/15 backdrop-blur-sm text-white border border-white/30 px-8 py-4 rounded-full font-semibold text-base hover:bg-white/25 transition-colors whitespace-nowrap cursor-pointer min-h-[52px] w-full sm:w-auto">
                Our Services
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── About ── */}
      <section id="about" className="py-16 sm:py-24 bg-[#F7FAFC]" style={{ contentVisibility: 'auto', containIntrinsicSize: '0 600px' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-6 py-2 border-2 border-[#4299E1] text-[#4299E1] rounded-full font-medium text-sm mb-4">Our Story</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#1A365D] mb-4">About Us</h2>
          </div>
          <div className="bg-white rounded-3xl shadow-lg p-8 sm:p-12">
            <div className="flex justify-center mb-8">
              <img
                src="https://public.readdy.ai/ai/img_res/7e7db0da051c2cb24235d3aade925718.jpg"
                alt="Our Team"
                className="w-32 h-32 rounded-full object-cover shadow-md"
                loading="lazy"
                decoding="async"
                width={128}
                height={128}
              />
            </div>
            <div className="space-y-6 text-[#2D3748] leading-relaxed text-base sm:text-lg">
              <p>In the heart of Orlando—where families vacation, businesses grow, and travelers search for unforgettable stays—our company was born with a simple promise: to elevate every space we touch.</p>
              <p>What began as a small, dedicated team serving a handful of vacation homes quickly grew into a trusted cleaning partner across Orlando and surrounding areas. Homeowners, property managers, and business owners recognized something different in our work: an obsession with detail, reliability, and the kind of care that makes guests feel instantly at home.</p>
              <div className="relative py-6 my-8">
                <div className="absolute left-0 top-0 text-8xl text-[#FF6B35]/20 font-serif">"</div>
                <p className="text-xl sm:text-2xl text-[#FF6B35] font-medium italic text-center relative z-10 px-8 sm:px-12">Excellence, consistency, and genuine pride in our craft.</p>
              </div>
              <p>Today, we proudly specialize in vacation homes, regular homes, offices, and high&#8209;end Airbnb properties, delivering spotless results that match the high expectations of Central Florida's vibrant hospitality scene.</p>
              <p>In a city built on magical experiences, we make sure your space reflects that magic too.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Services ── */}
      <section id="services" className="py-16 sm:py-24 bg-white" style={{ contentVisibility: 'auto', containIntrinsicSize: '0 700px' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#1A365D] mb-4">Our Services</h2>
            <p className="text-lg sm:text-xl text-[#2D3748]/70 max-w-2xl">We offer complete cleaning solutions to meet all your needs</p>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            <ServiceCard
              icon="ri-home-4-line" title="Residential Cleaning"
              bgFrom="from-[#EBF8FF]" iconBg="bg-[#4299E1]/20" iconColor="text-[#4299E1]"
              items={['Full cleaning of all rooms','Sanitized kitchens and bathrooms','Vacuuming and floor cleaning','Dust removal and organization']}
            />
            <ServiceCard
              icon="ri-building-line" title="Commercial Cleaning"
              bgFrom="from-[#EBF8FF]" iconBg="bg-[#4299E1]/20" iconColor="text-[#4299E1]" mt
              items={['Offices and corporate spaces','Retail stores and commercial establishments','After-hours cleaning','Scheduled regular maintenance','Disinfection of common areas']}
            />
            <ServiceCard
              icon="ri-sparkling-line" title="Deep Cleaning"
              bgFrom="from-[#EBF8FF]" iconBg="bg-[#4299E1]/20" iconColor="text-[#4299E1]"
              items={['Post-construction cleaning','Move-in / move-out cleaning','Carpet and upholstery cleaning','Window and glass cleaning']}
            />
            <ServiceCard
              icon="ri-star-line" title="Airbnb High-End"
              badge="Premium Service" badgeColor="text-[#FF6B35]"
              bgFrom="from-[#FFF5F0]" borderClass="border border-[#FF6B35]/20"
              iconBg="bg-[#FF6B35]/15" iconColor="text-[#FF6B35]" mt
              items={['5-star guest-ready preparation','Luxury linen & towel setup','Restocking of amenities','Inspection & photo report','Same-day turnaround available']}
            />
          </div>
        </div>
      </section>

      {/* ── Reviews — carrega só quando entra na viewport ── */}
      <div ref={reviewsView.ref}>
        {reviewsView.inView && (
          <Suspense fallback={<SectionSkeleton />}>
            <ReviewsSection />
          </Suspense>
        )}
      </div>

      {/* ── FAQ — carrega só quando entra na viewport ── */}
      <div ref={faqView.ref}>
        {faqView.inView && (
          <Suspense fallback={<SectionSkeleton />}>
            <FaqSection onContact={() => scrollToSection('contact')} />
          </Suspense>
        )}
      </div>

      {/* ── Contact — carrega só quando entra na viewport ── */}
      <div ref={contactView.ref}>
        {contactView.inView && (
          <Suspense fallback={<SectionSkeleton />}>
            <ContactSection />
          </Suspense>
        )}
      </div>

      {/* ── Footer — carrega só quando entra na viewport ── */}
      <div ref={footerView.ref}>
        {footerView.inView && (
          <Suspense fallback={<div className="h-32 bg-[#0F2942]" />}>
            <FooterSection onScrollTo={scrollToSection} />
          </Suspense>
        )}
      </div>

    </div>
  );
}